import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Coin } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Tipo de retorno para paginación server-side
 */
export interface CoinPageResponse {
  items: Coin[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class CoinService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/coins`;
  }

  /**
   * Normaliza el status de mayúsculas a minúsculas (ACTIVE -> active)
   */
  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToCoin(item: any): Coin {
    return {
      id: item.id?.toString() || '',
      code: item.code || '',
      name: item.name || '',
      symbol: item.symbol || '',
      status: this.normalizeStatus(item.status),
    };
  }

  /**
   * Obtiene todas las monedas (con paginación del backend)
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<CoinPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.apiUrl, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((item: any) => this.mapToCoin(item)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  /**
   * Obtiene una moneda por ID
   */
  getById(id: string): Observable<Coin> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToCoin(response.data)));
  }

  /**
   * Crea una nueva moneda
   * La API devuelve solo { message: "..." } sin data
   */
  create(data: Omit<Coin, 'id'>): Observable<{ message: string }> {
    return this.httpClient
      .post<{ success: boolean; message: string }>(this.apiUrl, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Moneda creada exitosamente',
        })),
      );
  }

  /**
   * Actualiza una moneda existente
   * La API devuelve solo { message: "..." } sin data
   */
  update(id: string, data: Partial<Coin>): Observable<{ message: string }> {
    return this.httpClient
      .put<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Moneda actualizada exitosamente',
        })),
      );
  }

  /**
   * Elimina una moneda
   * La API devuelve solo { message: "..." } sin data
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Moneda eliminada exitosamente',
        })),
      );
  }

  /**
   * Obtiene el historial de acciones de una moneda
   */
  getHistory(id: string, page = 0, size = 20): Observable<CoinHistoryAction[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.httpClient
      .get<ApiResponse<PageableData>>(`${this.apiUrl}/${id}/history`, {
        params,
      })
      .pipe(
        map((response) => {
          const items = response.data?.content || [];
          return items.map((action: any) => ({
            id: action.id,
            date: action.date,
            action: action.action || 'Acción',
            user: action.user || 'Usuario',
            ip: action.ip || '',
            device: action.device || '',
            browser: action.browser || '',
            location: action.location || '',
          }));
        }),
      );
  }

  /**
   * Actualiza el status de una moneda
   * La API devuelve solo { message: "..." } sin data
   */
  updateStatus(
    id: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  /**
   * Exporta la lista de monedas a PDF
   * La API genera y descarga el PDF directamente
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de monedas')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/coins`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Exporta la lista de monedas a Excel
   * La API genera y descarga el archivo Excel directamente
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de monedas')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/excel/coins`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Exporta el historial de una moneda a PDF
   */
  exportHistoryPdf(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial monedas',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/coins/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de una moneda a Excel
   */
  exportHistoryExcel(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial monedas',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/coins/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}

// Interface for history actions
export interface CoinHistoryAction {
  id: number;
  date: Date;
  action: string;
  user: string;
  ip: string;
  device: string;
  browser: string;
  location: string;
}
